import React, { Fragment, useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "./components/CheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import config from "./utils/config";
import { useCartContext } from "./CartContext";
import formatPrice from "./utils/formatPrice";
import { Transition } from "@headlessui/react";
import LoadingSpinner from "./components/LoadingSpinner";
import SvgLogo from "./SvgLogo";
import "./index.css";
import LineItem from "./components/LineItem";
import LineItemMobile from "./components/LineItemMobile";
import trackingService from "./analytics/trackingService";
import BundleItem from "./components/BundleItem";
import BundleItemMobile from "./components/BundleItemMobile";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import GiftItem from "./components/GiftItem";
import GiftItemMobile from "./components/GiftItemMobile";
import FreeGift from "./components/FreeGift";
import FreeGiftMobile from "./components/FreeGiftMobile";
import CombinedCodeForm from "./components/CombinedCodeForm";
import ErrorAlert from "./components/ErrorAlert";
import { CharityDonation, getCharityDonation } from "./utils/getCharityDonation";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY ?? "").catch(
  () => {
    return null;
  },
);

// Session-scoped memory of "the shopper tapped a donation in this visit",
// keyed by cart id. sessionStorage dies with the tab, which is exactly the
// lifetime a donation choice should have.
const DONATION_SESSION_KEY = "bushbuck-checkout-donation-cart";

function wasDonationChosenThisSession(cartId: string | undefined) {
  try {
    return !!cartId && window.sessionStorage.getItem(DONATION_SESSION_KEY) === cartId;
  } catch {
    return false;
  }
}

function rememberDonationChoice(cartId: string | undefined, chosen: boolean) {
  try {
    if (chosen && cartId) window.sessionStorage.setItem(DONATION_SESSION_KEY, cartId);
    else window.sessionStorage.removeItem(DONATION_SESSION_KEY);
  } catch {
    // storage unavailable: the choice simply will not survive a reload
  }
}

const Checkout = () => {
  const { checkout, checkoutTotals, totalsLoading, totalsError, updateCharityDonation } = useCartContext();
  const [showDetails, setShowDetails] = useState(false);
  const [selectedStripePaymentMethod, setSelectedStripePaymentMethod] =
    useState("card");
  const [selectedDonationOption, setSelectedDonationOption] = useState<string | null>(null);
  const [otherDonationAmount, setOtherDonationAmount] = useState("");
  const [charityDonation, setCharityDonation] = useState<CharityDonation | null>(null);
  const [isCharityDonationExpanded, setIsCharityDonationExpanded] = useState(false);
  const [persistedDonationAmount, setPersistedDonationAmount] = useState(0);
  const [selectedRoundUp, setSelectedRoundUp] = useState({ amount: 0, total: 0 });
  const savedDonationRef = useRef<string | null>(null);
  const hasDonationInteractionRef = useRef(false);
  const donationUpdateQueueRef = useRef(Promise.resolve());
  const checkoutTotalInCents = checkoutTotals
    ? Math.round(Number(checkoutTotals.total) * 100)
    : 0;
  // Round up the order total, excluding the saved donation.
  const checkoutTotalBeforeDonationInCents = Math.max(
    checkoutTotalInCents - Math.round(persistedDonationAmount * 100),
    0,
  );
  const calculatedRoundUpDonationAmount =
    ((100 - (checkoutTotalBeforeDonationInCents % 100)) % 100) / 100;
  const roundUpDonationAmount = selectedDonationOption === "round-up"
    ? selectedRoundUp.amount
    : calculatedRoundUpDonationAmount;
  const roundedUpCheckoutTotal = selectedDonationOption === "round-up"
    ? selectedRoundUp.total
    : checkoutTotalBeforeDonationInCents / 100 + calculatedRoundUpDonationAmount;
  const selectedDonationAmount = selectedDonationOption === "round-up"
    ? roundUpDonationAmount
    : Number(selectedDonationOption === "Other" ? otherDonationAmount : selectedDonationOption?.slice(1));
  const donationAmount = Number.isFinite(selectedDonationAmount) && selectedDonationAmount > 0
    ? selectedDonationAmount
    : 0;
  const checkoutTotalWithDonation = checkoutTotals
    ? (Number(checkoutTotals.total) - persistedDonationAmount + donationAmount).toFixed(2)
    : "";
  const donationOptions = charityDonation
    ? [...charityDonation.amounts, ...(charityDonation.allowCustomAmount ? ["Other"] : [])]
    : [];

  const selectDonationOption = (option: string | null) => {
    hasDonationInteractionRef.current = true;
    if (option === "round-up") {
      setSelectedRoundUp({
        amount: calculatedRoundUpDonationAmount,
        total: checkoutTotalBeforeDonationInCents / 100 + calculatedRoundUpDonationAmount,
      });
    }
    setSelectedDonationOption(option);
  };

  const clearDonation = () => {
    hasDonationInteractionRef.current = true;
    setSelectedDonationOption(null);
    setOtherDonationAmount("");
  };

  useEffect(() => {
    getCharityDonation().then(setCharityDonation);
  }, []);

  // A donation is an explicit choice for THIS checkout visit. The chosen
  // amount is saved on the Shopify cart (so the order can include it), which
  // means a cart that was abandoned after a tap still carries it days later.
  // On load: keep it only if it was chosen in this browser session (so a
  // reload does not lose the tap); otherwise remove it from the cart so the
  // shopper is never charged for something they did not pick this time.
  const hydratedDonationRef = useRef(false);
  useEffect(() => {
    if (hydratedDonationRef.current || hasDonationInteractionRef.current) return;
    if (!charityDonation || !checkout || !checkoutTotals) return;
    hydratedDonationRef.current = true;
    const persisted = checkout.charityDonation?.amount ?? 0;
    if (!(persisted > 0)) return;
    if (!wasDonationChosenThisSession(checkout.cartId)) {
      savedDonationRef.current = null;
      updateCharityDonation(null).catch(() => {});
      return;
    }
    setPersistedDonationAmount(persisted);
    savedDonationRef.current = JSON.stringify({ sku: charityDonation.sku, amount: persisted });
    const fixedOption = charityDonation.amounts.find(
      (amount) => Math.abs(Number(String(amount).replace("$", "")) - persisted) < 0.005,
    );
    if (fixedOption) {
      setSelectedDonationOption(fixedOption);
      return;
    }
    const totalBeforeDonationInCents = Math.max(
      Math.round(Number(checkoutTotals.total) * 100) - Math.round(persisted * 100),
      0,
    );
    const roundUp = ((100 - (totalBeforeDonationInCents % 100)) % 100) / 100;
    if (Math.abs(roundUp - persisted) < 0.005) {
      setSelectedRoundUp({ amount: persisted, total: totalBeforeDonationInCents / 100 + persisted });
      setSelectedDonationOption("round-up");
      return;
    }
    if (charityDonation.allowCustomAmount) {
      setOtherDonationAmount(persisted.toFixed(2));
      setSelectedDonationOption("Other");
      return;
    }
    savedDonationRef.current = null;
    updateCharityDonation(null).catch(() => {});
  }, [charityDonation, checkout, checkoutTotals, updateCharityDonation]);

  useEffect(() => {
    if (!charityDonation?.sku || !hasDonationInteractionRef.current) return;

    const donation = donationAmount > 0
      ? { sku: charityDonation.sku, amount: donationAmount }
      : null;
    const donationKey = JSON.stringify(donation);
    if (savedDonationRef.current === donationKey) return;

    savedDonationRef.current = donationKey;
    rememberDonationChoice(checkout?.cartId, donation !== null);
    donationUpdateQueueRef.current = donationUpdateQueueRef.current
      .then(() => updateCharityDonation(donation))
      .then(() => setPersistedDonationAmount(donationAmount))
      .catch(() => {
        savedDonationRef.current = null;
      });
  }, [charityDonation, checkout?.cartId, donationAmount, updateCharityDonation]);

  useEffect(() => {
    if (!checkout || !checkoutTotals) {
      return;
    }
    Sentry.addBreadcrumb({
      category: "checkout",
      message: "Begin Checkout",
    });
    Sentry.setContext("checkout", checkout);
    trackingService
      .trackEvent("begin_checkout", {
        checkout: checkout,
        totals: checkoutTotals,
      })
      .then(() => {});
  }, [checkout, checkoutTotals]);

  const handleShowDetails = () => {
    setShowDetails(true);
  };

  const isMobileSummaryLoading = totalsLoading || !checkoutTotals;

  if (!checkout) {
    return null;
  }

  return (
    <>
      {!checkout.completed && (
        <div className="bg-white">
          {false && (
            <div className="fixed top-0 z-[102] left-0 w-full transform transition-all duration-500 translate-y-0">
              <div className="bg-secondary-500 absolute w-full z-10 flex flex-col justify-center h-10 text-white">
                <div className="flex justify-center text-[0.9rem] font-bold items-center mx-5 lg:mx-10 xl:mx-[90px]"></div>
              </div>
            </div>
          )}
          <div
            className="overflow-hidden fixed left-0 top-0 hidden h-full w-1/2 bg-gray-50 lg:block"
            aria-hidden="true"
          />
          <div
            className="fixed right-0 top-0 hidden h-full w-1/2 bg-white lg:block"
            aria-hidden="true"
          />
          <div className="fixed inset-x-0 top-0 flex flex-col z-[101] text-sm font-medium text-gray-900 lg:hidden">
            <div className="relative z-[102] border-t border-gray-200 bg-white px-4 sm:px-6">
              <div className="mx-auto">
                <div className="flex w-full items-center py-4 font-medium focus:outline-0">
                  <div className="mr-auto text-base">
                    <div className="flex items-center justify-flex-start text-lg font-bold text-gray-500">
                      <a
                        href={`${config.storeUrl}/cart`}
                        aria-label="Bushbuck Logo"
                        className={`flex items-center justify-center cursor-pointer text-gray-900 h-6 ${showDetails && "pointer-events-none"}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className={`w-5 h-5 mr-2 text-gray-400 transition ${showDetails && "-translate-x-4 opacity-0"}`}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
                          />
                        </svg>
                        <span
                          className={`text-sm transition ${showDetails && "opacity-0"}`}
                        >
                          Back
                        </span>
                      </a>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center mr-2 text-sm text-gray-400 underline decoration-dotted"
                  >
                    <span className="mr-2">
                      {showDetails ? "Close" : "Details"}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className={`w-3 h-3 transition-all transform ${showDetails && "rotate-180"}`}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <Transition.Root as={Fragment} show={showDetails}>
              <div>
                <Transition.Child
                  as={Fragment}
                  enter="transition-opacity ease-linear duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity ease-linear duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div
                    onClick={() => setShowDetails(false)}
                    className="fixed inset-0 bg-black bg-opacity-25"
                  />
                </Transition.Child>

                <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="-translate-y-full"
                  enterTo="translate-y-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-y-0"
                  leaveTo="-translate-y-full"
                >
                  <div className="relative bg-white px-4 py-6 sm:px-6 z-[101] overflow-y-scroll">
                    <div className="max-w-lg mx-auto mb-8">
                      <ul className="-my-6 divide-y divide-gray-200">
                        {checkout?.lineItems.map((line, index) => (
                          <LineItemMobile
                            key={`${line.variantId}-${line.discountNote ?? "standard"}-${index}`}
                            lineItem={line}
                            currencyCode={checkout.currencyCode}
                          />
                        ))}
                        {checkout.bundleItems.map((bundle) => (
                          <BundleItemMobile
                            key={bundle.id}
                            bundle={bundle}
                            currencyCode={checkout.currencyCode}
                          />
                        ))}
                        {checkout?.giftItems.map((giftItem) => (
                          <GiftItemMobile
                            key={giftItem.id}
                            giftItem={giftItem}
                            currencyCode={checkout.currencyCode}
                          />
                        ))}
                        {checkout?.freeGiftTier && checkoutTotals && (parseFloat(checkoutTotals.total) > checkout?.freeGiftTier.thresholdAmount || checkout.freeGiftTier.isProductGift) && (
                          <FreeGiftMobile
                            freeGift={checkout?.freeGiftTier}
                            currencyCode={checkout.currencyCode}
                          />
                        )}
                      </ul>
                    </div>
                    {totalsError && (
                      <div className="mt-5">
                        <ErrorAlert message={totalsError} />
                      </div>
                    )}
                    {checkoutTotals && (
                      <dl className="mx-auto max-w-lg space-y-6 mt-6 border-t border-gray-200 pt-6">
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-600">Cart Total</dt>
                          <dd>
                            {formatPrice(
                              checkoutTotals.carttotal,
                              checkout.currencyCode,
                            )}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-600">Savings</dt>
                          <dd>
                            {formatPrice(
                              checkoutTotals.discounts,
                              checkout.currencyCode,
                            )}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-600">Subtotal</dt>
                          <dd>
                            {formatPrice(
                              checkoutTotals.subtotal,
                              checkout.currencyCode,
                            )}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-600">Shipping</dt>
                          <dd>
                            {formatPrice(
                              checkoutTotals.shipping,
                              checkout.currencyCode,
                            )}
                          </dd>
                        </div>
                        {donationAmount > 0 && (
                          <div className="flex items-center justify-between">
                            <dt className="text-gray-600">Donation</dt>
                            <dd>
                              {formatPrice(
                                donationAmount.toFixed(2),
                                checkout.currencyCode,
                              )}
                            </dd>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-600">Total</dt>
                          <dd>
                            {formatPrice(
                              checkoutTotalWithDonation,
                              checkout.currencyCode,
                            )}
                          </dd>
                        </div>
                        <div className="text-sm text-gray-700 font-light">
                          including{" "}
                          {formatPrice(
                            checkoutTotals.tax,
                            checkout.currencyCode,
                          )}{" "}
                          in taxes
                        </div>
                        {checkoutTotals?.giftCard && (
                          <>
                            <div className="flex items-center justify-between">
                              <dt className="text-gray-600">
                                Gift card applied
                              </dt>
                              {!totalsLoading && (
                                <dd className="text-gray-900">
                                  {formatPrice(
                                    checkoutTotals.giftCard,
                                    checkout.currencyCode,
                                  )}
                                </dd>
                              )}
                              {totalsLoading && (
                                <dd>
                                  <LoadingSpinner/>
                                </dd>
                              )}
                            </div>
                            {checkoutTotals?.amountDue && (
                              <div className="flex items-center justify-between">
                                <dt className="text-gray-600">Amount due</dt>
                                {!totalsLoading && (
                                  <dd className="text-gray-900">
                                    {formatPrice(
                                      checkoutTotals.amountDue,
                                      checkout.currencyCode,
                                    )}
                                  </dd>
                                )}
                                {totalsLoading && (
                                  <dd>
                                    <LoadingSpinner/>
                                  </dd>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </dl>
                    )}
                  </div>
                </Transition.Child>
              </div>
            </Transition.Root>
          </div>
          <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-x-16 lg:grid-cols-2 lg:px-8 xl:gap-x-48">
            <h1 className="sr-only">Order information</h1>

            <section
              aria-labelledby="summary-heading"
              className="px-4 pb-10 pt-8 sm:px-6 lg:col-start-2 lg:row-start-1 lg:bg-transparent lg:px-0 lg:pb-16"
            >
              <div className="flex flex-col justify-center items-center lg:hidden py-16">
                <img
                  src="/bushbuck-logo-square.png"
                  alt="Bushbuck"
                  className="cursor-pointer w-36 mb-3 rounded-md p-4 bg-gray-100 object-cover object-center"
                  onClick={() => setShowDetails(true)}
                />
                <h2 className="text-lg font-medium text-gray-500 mb-2">
                  Pay Bushbuck
                </h2>
                {checkoutTotals && (
                  <h3 className="text-3xl font-medium">
                    {formatPrice(checkoutTotalWithDonation, checkout.currencyCode)}
                  </h3>
                )}
                {isMobileSummaryLoading ? (
                  <div className="mt-6 flex h-11 items-center">
                    <LoadingSpinner size="large" />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="rounded-3xl mt-2 bg-gray-100 font-medium items-center gap-2 text-sm text-black flex px-6 py-3 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    onClick={() => setShowDetails(true)}
                  >
                    View details
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3"
                      stroke="currentColor"
                      className="w-3 h-3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </button>
                )}
              </div>
              {checkout && checkoutTotals && (
                <div className="mx-auto max-w-lg lg:max-w-none">
                  <PayPalScriptProvider
                    options={{
                      clientId: config.paypalClientId,
                      currency: config.currencyCode,
                      intent: "capture",
                      disableFunding: "paylater,venmo,card",
                    }}
                  >
                    <Elements
                      // A new cart represents a new payment attempt. Totals and payment
                      // method changes are applied to this existing Elements instance so
                      // Stripe can retain any details the customer has already entered.
                      key={checkout.cartId}
                      stripe={stripePromise}
                      options={{
                        mode: "payment",
                        amount:
                          checkoutTotals.amountDueInCents > 0
                            ? checkoutTotals.amountDueInCents
                            : checkoutTotals.totalInCents,
                        currency: checkout.currencyCode.toLowerCase(),
                        appearance: config.stripeTheme,
                        fonts: config.fonts,
                        paymentMethodTypes: [selectedStripePaymentMethod],
                      }}
                    >
                      <CheckoutForm
                        onShowDetails={handleShowDetails}
                        stripePromise={stripePromise}
                        selectedStripePaymentMethod={
                          selectedStripePaymentMethod
                        }
                        setSelectedStripePaymentMethod={
                          setSelectedStripePaymentMethod
                        }
                        donationOption={selectedDonationOption}
                        onDonationOptionChange={selectDonationOption}
                        otherDonationAmount={otherDonationAmount}
                        onOtherDonationAmountChange={(amount) => {
                          setOtherDonationAmount(amount);
                        }}
                        donationAmount={donationAmount}
                        roundUpDonationAmount={roundUpDonationAmount}
                        roundedUpCheckoutTotal={roundedUpCheckoutTotal}
                        checkoutTotalWithDonation={checkoutTotalWithDonation}
                        charityDonation={charityDonation}
                      />
                    </Elements>
                  </PayPalScriptProvider>
                </div>
              )}
            </section>
            <div className="hidden lg:flex flex-col px-4 pb-36 pt-8 sm:px-6 lg:col-start-1 lg:row-start-1 lg:px-0 lg:pb-16">
              <div className="flex items-center justify-flex-start mb-4 text-lg font-bold text-gray-500">
                <a
                  href={`${config.storeUrl}/cart`}
                  aria-label="Bushbuck Logo"
                  className={`flex group items-center justify-center cursor-pointer text-[#111] h-10`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 mr-2 text-gray-400 group-hover:text-gray-900"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
                    />
                  </svg>
                  <SvgLogo
                    color="inherit"
                    className="absolute left-16 transition-opacity duration-200 group-hover:opacity-0"
                    style={{ width: "140px", height: "36px", margin: "auto" }}
                  />
                  <span className="absolute text-sm left-16 leading-1 hidden duration-200 group-hover:inline transition-opacity">
                    Back
                  </span>
                </a>
              </div>
              <h2
                id="summary-heading"
                className="text-lg font-bold text-gray-500 mb-2"
              >
                Pay Bushbuck
              </h2>

              {checkoutTotals && (
                <h3 className="text-4xl font-medium">
                  {formatPrice(checkoutTotalWithDonation, checkout.currencyCode)}
                </h3>
              )}

              <ul className="divide-y divide-gray-200 text-sm font-medium text-gray-900">
                {checkout.lineItems.map((lineItem, index) => (
                  <LineItem
                    key={`${lineItem.variantId}-${lineItem.discountNote ?? "standard"}-${index}`}
                    lineItem={lineItem}
                    currencyCode={checkout.currencyCode}
                  />
                ))}
                {checkout.bundleItems.map((bundle) => (
                  <BundleItem
                    key={bundle.id}
                    bundle={bundle}
                    currencyCode={checkout.currencyCode}
                  />
                ))}
                {checkout?.giftItems.map((giftItem) => (
                  <GiftItem
                    key={giftItem.id}
                    giftItem={giftItem}
                    currencyCode={checkout.currencyCode}
                  />
                ))}
                {checkout?.freeGiftTier && checkoutTotals && (parseFloat(checkoutTotals.total) > checkout?.freeGiftTier.thresholdAmount || checkout.freeGiftTier.isProductGift) && (
                  <FreeGift
                    freeGift={checkout?.freeGiftTier}
                    currencyCode={checkout.currencyCode}
                  />
                )}
              </ul>

              <dl className="hidden space-y-6 border-t border-gray-200 pt-6 text-sm font-medium text-gray-900 lg:block">
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Cart Total</dt>
                  {!totalsLoading && checkoutTotals?.carttotal && (
                    <dd>
                      {formatPrice(
                        checkoutTotals.carttotal,
                        checkout.currencyCode,
                      )}
                    </dd>
                  )}
                  {totalsLoading && (
                    <dd>
                      <LoadingSpinner />
                    </dd>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Savings</dt>
                  {!totalsLoading && checkoutTotals?.discounts && (
                    <dd className="text-gray-900">
                      {formatPrice(
                        checkoutTotals.discounts,
                        checkout.currencyCode,
                      )}
                    </dd>
                  )}
                  {totalsLoading && (
                    <dd>
                      <LoadingSpinner />
                    </dd>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Subtotal</dt>
                  {!totalsLoading && checkoutTotals?.subtotal && (
                    <dd>
                      {formatPrice(
                        checkoutTotals.subtotal,
                        checkout.currencyCode,
                      )}
                    </dd>
                  )}
                  {totalsLoading && (
                    <dd>
                      <LoadingSpinner />
                    </dd>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <CombinedCodeForm />
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                  <dt className="text-gray-600">Shipping</dt>
                  {!totalsLoading &&
                    checkout.shippingMethod &&
                    checkoutTotals?.shipping && (
                      <dd className="text-gray-900">
                        {formatPrice(
                          checkoutTotals.shipping,
                          checkout.currencyCode,
                        )}
                      </dd>
                    )}
                  {totalsLoading && (
                    <dd>
                      <LoadingSpinner />
                    </dd>
                  )}
                  {!checkout.shippingMethod && (
                    <dd className="text-gray-600">Enter shipping address</dd>
                  )}
                </div>
                {donationAmount > 0 && (
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-600">Donation</dt>
                    <dd className="text-gray-900">
                      {formatPrice(donationAmount.toFixed(2), checkout.currencyCode)}
                    </dd>
                  </div>
                )}
                {charityDonation && <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <button
                    type="button"
                    aria-expanded={isCharityDonationExpanded}
                    onClick={() => setIsCharityDonationExpanded((expanded) => !expanded)}
                    className="grid w-full grid-cols-3 items-center gap-4 border-b border-gray-200 px-5 py-4 text-left md:flex"
                  >
                    <div className="col-span-1 flex items-center justify-center self-stretch md:col-auto md:min-w-[20%] md:flex-1">
                      <img
                        src={charityDonation.imageUrl}
                        alt={`${charityDonation.name} logo`}
                        className="max-h-24 w-full object-contain"
                      />
                    </div>
                    <div className="col-span-2 flex min-w-0 items-center gap-3 md:col-auto md:min-w-[calc(80%-1rem)] md:flex-1">
                      <h3 className="text-sm font-semibold leading-snug text-gray-900">
                        Donate to {charityDonation.name}
                      </h3>
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${isCharityDonationExpanded ? "rotate-180" : ""}`}
                      >
                        <path d="m5 7.5 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </button>
                  {isCharityDonationExpanded && <>
                    <div className="border-b border-gray-200">
                      <div className="min-w-0 px-4 py-4">
                        <div className="space-y-2 text-xs leading-relaxed text-gray-500">
                          {charityDonation.about.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>}

                  <div
                    role="radiogroup"
                    aria-label="Donation amount"
                    className="space-y-3 px-5 pb-5 pt-4"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selectedDonationOption === "round-up"}
                      onClick={() => selectDonationOption("round-up")}
                      className={`relative flex w-full cursor-pointer rounded-3xl border p-4 text-left focus:outline-none ${
                        selectedDonationOption === "round-up"
                          ? "z-10 border-gray-600 bg-gray-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 bg-white ${
                          selectedDonationOption === "round-up"
                            ? "border-gray-600"
                            : "border-gray-400"
                        }`}
                        aria-hidden="true"
                      >
                        {selectedDonationOption === "round-up" && (
                          <span className="h-2 w-2 rounded-full bg-gray-600" />
                        )}
                      </span>
                      <span className="ml-3 grid min-w-0 grid-cols-2 items-center gap-2">
                        <span className="min-w-0 text-sm font-medium text-gray-900 pr-5">
                          Round up to the nearest dollar
                        </span>
                        <span className="min-w-0 text-xs font-light text-gray-700">
                          {totalsLoading ? (
                            <span aria-hidden="true" className="invisible">
                              Add <span className="font-semibold">${roundUpDonationAmount.toFixed(2)}</span> to bring your total to ${roundedUpCheckoutTotal.toFixed(2)}
                            </span>
                          ) : (
                            <>
                              Add <span className="font-semibold">${roundUpDonationAmount.toFixed(2)}</span> to bring your total to ${roundedUpCheckoutTotal.toFixed(2)}
                            </>
                          )}
                        </span>
                      </span>
                    </button>
                    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${donationOptions.length}, minmax(0, 1fr))` }}>
                      {donationOptions.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          role="radio"
                          aria-checked={selectedDonationOption === amount}
                          onClick={() => selectDonationOption(amount)}
                          className={`relative flex cursor-pointer items-center rounded-3xl border bg-gray-50 p-3 text-left focus:outline-none ${
                            selectedDonationOption === amount
                              ? "z-10 border-gray-600"
                              : "border-gray-200"
                          }`}
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 bg-white ${
                              selectedDonationOption === amount
                                ? "border-gray-600"
                                : "border-gray-400"
                            }`}
                            aria-hidden="true"
                          >
                            {selectedDonationOption === amount && (
                              <span className="h-2 w-2 rounded-full bg-gray-600" />
                            )}
                          </span>
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            {amount}
                          </span>
                        </button>
                      ))}
                    </div>
                    {selectedDonationOption === "Other" && (
                      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <span className="select-none text-sm font-medium text-gray-500">$</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={otherDonationAmount}
                          onChange={(event) => {
                            setOtherDonationAmount(event.target.value);
                          }}
                          placeholder="Enter amount"
                          aria-label="Other donation amount"
                          className="flex-1 appearance-none bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                      </div>
                    )}
                    {selectedDonationOption && (
                      <button
                        type="button"
                        onClick={() => {
                          clearDonation();
                        }}
                        className="w-full text-center text-xs text-gray-500 underline underline-offset-2 transition-colors hover:text-gray-900"
                      >
                        No thanks, I&apos;ll skip the donation
                      </button>
                    )}
                  </div>
                  {donationAmount > 0 && (
                    <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-secondary-50 px-5 py-4">
                      <div>
                        <p className="text-xs text-gray-500">Donation added</p>
                        <p className="text-base font-semibold text-gray-900">
                          +${donationAmount.toFixed(2)}{" "}
                          <span className="text-sm font-normal text-gray-500">
                            to {charityDonation.name}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>}
                <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                  <dt className="text-base">Total</dt>
                  {!totalsLoading && checkoutTotals?.total && (
                    <dd className="text-gray-900">
                      {formatPrice(checkoutTotalWithDonation, checkout.currencyCode)}
                    </dd>
                  )}
                  {totalsLoading && (
                    <dd>
                      <LoadingSpinner />
                    </dd>
                  )}
                </div>
              </dl>
              {totalsError && (
                <div className="mt-5">
                  <ErrorAlert message={totalsError} />
                </div>
              )}
              {!totalsLoading && checkoutTotals?.tax && (
                <span className="text-sm text-gray-700 mt-3 font-light">
                  including{" "}
                  {formatPrice(checkoutTotals.tax, checkout.currencyCode)} in
                  taxes
                </span>
              )}

              {checkoutTotals?.giftCard && (
                <dl className="hidden space-y-6 border-t border-gray-200 mt-6 pt-6 text-sm font-medium text-gray-900 lg:block">
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-600">Gift card applied</dt>
                    {!totalsLoading && (
                      <dd className="text-gray-900">
                        {formatPrice(
                          checkoutTotals.giftCard,
                          checkout.currencyCode,
                        )}
                      </dd>
                    )}
                    {totalsLoading && (
                      <dd>
                        <LoadingSpinner />
                      </dd>
                    )}
                  </div>
                  {checkoutTotals?.amountDue && (
                    <div className="flex items-center justify-between">
                      <dt className="text-gray-600">Amount due</dt>
                      {!totalsLoading && (
                        <dd className="text-gray-900">
                          {formatPrice(
                            checkoutTotals.amountDue,
                            checkout.currencyCode,
                          )}
                        </dd>
                      )}
                      {totalsLoading && (
                        <dd>
                          <LoadingSpinner />
                        </dd>
                      )}
                    </div>
                  )}
                </dl>
              )}
              <div className="flex-1"></div>
              <div className="flex justify-center gap-10 pt-5">
                <div className="flex gap-1 text-sm items-center text-gray-400 font-light">
                  <span>Powered by</span>
                  <svg
                    className="text-gray-500"
                    fill="currentColor"
                    focusable="false"
                    width="33"
                    height="15"
                    role="img"
                    aria-labelledby="stripe-title"
                  >
                    <title id="stripe-title">Stripe</title>
                    <g fillRule="evenodd">
                      <path d="M32.956 7.925c0-2.313-1.12-4.138-3.261-4.138-2.15 0-3.451 1.825-3.451 4.12 0 2.719 1.535 4.092 3.74 4.092 1.075 0 1.888-.244 2.502-.587V9.605c-.614.307-1.319.497-2.213.497-.876 0-1.653-.307-1.753-1.373h4.418c0-.118.018-.588.018-.804zm-4.463-.859c0-1.02.624-1.445 1.193-1.445.55 0 1.138.424 1.138 1.445h-2.33zM22.756 3.787c-.885 0-1.454.415-1.77.704l-.118-.56H18.88v10.535l2.259-.48.009-2.556c.325.235.804.57 1.6.57 1.616 0 3.089-1.302 3.089-4.166-.01-2.62-1.5-4.047-3.08-4.047zm-.542 6.225c-.533 0-.85-.19-1.066-.425l-.009-3.352c.235-.262.56-.443 1.075-.443.822 0 1.391.922 1.391 2.105 0 1.211-.56 2.115-1.39 2.115zM18.04 2.766V.932l-2.268.479v1.843zM15.772 3.94h2.268v7.905h-2.268zM13.342 4.609l-.144-.669h-1.952v7.906h2.259V6.488c.533-.696 1.436-.57 1.716-.47V3.94c-.289-.108-1.346-.307-1.879.669zM8.825 1.98l-2.205.47-.009 7.236c0 1.337 1.003 2.322 2.34 2.322.741 0 1.283-.135 1.581-.298V9.876c-.289.117-1.716.533-1.716-.804V5.865h1.716V3.94H8.816l.009-1.96zM2.718 6.235c0-.352.289-.488.767-.488.687 0 1.554.208 2.241.578V4.202a5.958 5.958 0 0 0-2.24-.415c-1.835 0-3.054.957-3.054 2.557 0 2.493 3.433 2.096 3.433 3.17 0 .416-.361.552-.867.552-.75 0-1.708-.307-2.467-.723v2.15c.84.362 1.69.515 2.467.515 1.879 0 3.17-.93 3.17-2.548-.008-2.692-3.45-2.213-3.45-3.225z"></path>
                    </g>
                  </svg>
                </div>
                <a
                  className="text-gray-400 font-light text-sm hover:text-gray-900 underline"
                  target="_blank"
                  href={`${config.storeUrl}/pages/privacy-policy`}
                  rel="noreferrer"
                >
                  Privacy
                </a>
                <a
                  className="text-gray-400 font-light text-sm hover:text-gray-900 underline"
                  target="_blank"
                  href={`${config.storeUrl}/pages/returns-exchanges`}
                  rel="noreferrer"
                >
                  Returns
                </a>
                <a
                  className="text-gray-400 font-light text-sm hover:text-gray-900 underline"
                  target="_blank"
                  href={`${config.storeUrl}/pages/contact-us`}
                  rel="noreferrer"
                >
                  Support
                </a>
              </div>
            </div>
          </div>
          <div className="flex lg:hidden justify-center gap-10">
            <a
              className="text-gray-400 font-light text-sm hover:text-gray-900 underline"
              href={`${config.storeUrl}/pages/privacy-policy`}
            >
              Privacy
            </a>
            <a
              className="text-gray-400 font-light text-sm hover:text-gray-900 underline"
              target="_blank"
              href={`${config.storeUrl}/pages/returns-exchanges`}
              rel="noreferrer"
            >
              Returns
            </a>
            <a
              className="text-gray-400 font-light text-sm hover:text-gray-900 underline"
              href={`${config.storeUrl}/pages/contact-us`}
            >
              Support
            </a>
          </div>
          <div className="flex gap-1 text-sm justify-center mt-4 items-center text-gray-400 font-light pb-4">
            <span>Powered by</span>
            <svg
              className="text-gray-500"
              fill="currentColor"
              focusable="false"
              width="33"
              height="15"
              role="img"
              aria-labelledby="stripe-title"
            >
              <title id="stripe-title">Stripe</title>
              <g fillRule="evenodd">
                <path d="M32.956 7.925c0-2.313-1.12-4.138-3.261-4.138-2.15 0-3.451 1.825-3.451 4.12 0 2.719 1.535 4.092 3.74 4.092 1.075 0 1.888-.244 2.502-.587V9.605c-.614.307-1.319.497-2.213.497-.876 0-1.653-.307-1.753-1.373h4.418c0-.118.018-.588.018-.804zm-4.463-.859c0-1.02.624-1.445 1.193-1.445.55 0 1.138.424 1.138 1.445h-2.33zM22.756 3.787c-.885 0-1.454.415-1.77.704l-.118-.56H18.88v10.535l2.259-.48.009-2.556c.325.235.804.57 1.6.57 1.616 0 3.089-1.302 3.089-4.166-.01-2.62-1.5-4.047-3.08-4.047zm-.542 6.225c-.533 0-.85-.19-1.066-.425l-.009-3.352c.235-.262.56-.443 1.075-.443.822 0 1.391.922 1.391 2.105 0 1.211-.56 2.115-1.39 2.115zM18.04 2.766V.932l-2.268.479v1.843zM15.772 3.94h2.268v7.905h-2.268zM13.342 4.609l-.144-.669h-1.952v7.906h2.259V6.488c.533-.696 1.436-.57 1.716-.47V3.94c-.289-.108-1.346-.307-1.879.669zM8.825 1.98l-2.205.47-.009 7.236c0 1.337 1.003 2.322 2.34 2.322.741 0 1.283-.135 1.581-.298V9.876c-.289.117-1.716.533-1.716-.804V5.865h1.716V3.94H8.816l.009-1.96zM2.718 6.235c0-.352.289-.488.767-.488.687 0 1.554.208 2.241.578V4.202a5.958 5.958 0 0 0-2.24-.415c-1.835 0-3.054.957-3.054 2.557 0 2.493 3.433 2.096 3.433 3.17 0 .416-.361.552-.867.552-.75 0-1.708-.307-2.467-.723v2.15c.84.362 1.69.515 2.467.515 1.879 0 3.17-.93 3.17-2.548-.008-2.692-3.45-2.213-3.45-3.225z"></path>
              </g>
            </svg>
          </div>
        </div>
      )}
      {checkout.completed && (
        <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8 mt-10">
          <div className="text-center">
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Order complete
            </h1>
            <p className="mt-6 text-base leading-7 text-gray-600">
              It looks like this checkout has already completed. if you're
              having trouble, get in touch with our support team.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href={config.storeUrl}
                className="rounded-md bg-gray-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              >
                Back to store
              </a>
              <a
                href={`${config.storeUrl}/pages/contact-us`}
                className="text-sm font-semibold text-gray-900"
              >
                Contact support <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>
        </main>
      )}
    </>
  );
};

export default Checkout;
